import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Plus, Trash2, X, AlertTriangle, Clock } from "lucide-react";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";

export const saveButtonClass = "w-full h-12 rounded-xl bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed";
export const primaryButtonClass = "inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white shadow-md shadow-sky-600/25 transition-colors hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const FIELD_LABELS = { name: "Név", gender: "Nem", birth_date: "Születési dátum", email: "E-mail-cím", phone: "Telefonszám" };
const toIso = (d) => (d || "").replace(/\./g, "-").replace(/-+$/, "").trim();

const Field = ({ label, missing, children, testId }) => (
  <div data-testid={testId}>
    <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700">
      {label}
      {missing && <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-800" data-testid={`${testId}-missing`}>Hiányzik</span>}
    </label>
    {children}
  </div>
);

const inputCls = (missing) => `${loginInputClass} ${missing ? "border-yellow-400 bg-yellow-50 focus:border-yellow-500 focus:ring-yellow-400/30" : ""}`;

export function ProfilTab({ onUserUpdate }) {
  const [form, setForm] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linked, setLinked] = useState([]);
  const [requests, setRequests] = useState([]);
  const [identifier, setIdentifier] = useState("");
  const [linking, setLinking] = useState(false);
  const [childOpen, setChildOpen] = useState(false);
  const [membership, setMembership] = useState(null);

  const load = async () => {
    try {
      const [p, l, r, m] = await Promise.all([
        axios.get(`${API}/ugyfelkapu/profile`, { params: { token: token() } }),
        axios.get(`${API}/competition/users/linked-accounts`, { params: { token: token() } }),
        axios.get(`${API}/ugyfelkapu/requests`, { params: { token: token() } }),
        axios.get(`${API}/ugyfelkapu/membership`, { params: { token: token() } }).catch(() => ({ data: { state: "none", code: null } })),
      ]);
      setForm({ ...p.data, birth_date: toIso(p.data.birth_date) });
      setLinked(l.data.linked_accounts || []);
      setRequests(r.data.requests || []);
      setMembership(m.data);
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
  };
  useEffect(() => { load(); }, []);

  if (!form) return <p className="py-16 text-center text-slate-600" data-testid="profile-loading">Betöltés...</p>;

  const missing = Object.keys(FIELD_LABELS).filter((f) => !String(form[f] || "").trim());
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: form.name, gender: form.gender, birth_date: form.birth_date, email: form.email, phone: form.phone };
      if (newPassword) body.new_password = newPassword;
      const res = await axios.put(`${API}/ugyfelkapu/profile`, body, { params: { token: token() } });
      setForm({ ...res.data.profile, birth_date: toIso(res.data.profile.birth_date) });
      setNewPassword("");
      onUserUpdate?.(res.data.profile);
      toast.success(newPassword ? "Profil és jelszó elmentve" : "Profil sikeresen elmentve");
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setSaving(false);
  };

  const link = async () => {
    if (!identifier.trim()) { toast.error("Add meg az azonosító kódot"); return; }
    setLinking(true);
    try {
      const res = await axios.post(`${API}/ugyfelkapu/link-requests`, { identifier }, { params: { token: token() } });
      toast.success(res.data.message);
      setIdentifier("");
      load();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setLinking(false);
  };

  const unlink = async (acc) => {
    if (!window.confirm(`Biztosan megszünteted a hozzárendelést: ${acc.name}?`)) return;
    try {
      await axios.delete(`${API}/competition/users/unlink-account/${acc.id}`, { params: { token: token() } });
      toast.success("Hozzárendelés megszüntetve");
      load();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
  };

  const pending = requests.filter((r) => r.status === "pending");

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6" data-testid="profil-tab">
      {membership?.state === "active" ? (
        <div className="rounded-2xl bg-emerald-100 px-5 py-4 text-base font-semibold text-slate-900 ring-1 ring-emerald-200" data-testid="profile-membership-banner" data-state="active">
          Aktív GLE-tagság – Tagsági kód: <span className="font-mono tracking-widest">{membership.code || "—"}</span>
        </div>
      ) : (
        <div className="rounded-2xl bg-slate-100 px-5 py-4 text-base font-semibold text-slate-800 ring-1 ring-slate-200" data-testid="profile-membership-banner" data-state="none">
          Nincs aktív GLE-tagság
        </div>
      )}
      {missing.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-yellow-300 bg-yellow-100 px-4 py-3 text-sm font-medium text-yellow-900" data-testid="profile-missing-banner">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-yellow-600" />
          <span>⚠️ Hiányzó profiladatok! Kérjük, töltsd ki a hiányzó adatokat, majd kattints a Mentés gombra. <span className="font-normal">({missing.map((m) => FIELD_LABELS[m]).join(", ")})</span></span>
        </div>
      )}

      <form onSubmit={save} className="rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100 space-y-5" data-testid="profile-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-slate-900">Saját profiladatok</h2>
          <span className="rounded-xl bg-slate-100 px-3 py-1.5 font-mono text-sm font-semibold tracking-wider text-slate-700" data-testid="profile-identifier-badge">Azonosító: {form.identifier || "—"}</span>
        </div>
        <Field label="Név" missing={missing.includes("name")} testId="profile-field-name">
          <input value={form.name} onChange={set("name")} className={inputCls(missing.includes("name"))} data-testid="profile-name-input" />
        </Field>
        <Field label="Nem" missing={missing.includes("gender")} testId="profile-field-gender">
          <select value={form.gender} onChange={set("gender")} className={inputCls(missing.includes("gender"))} data-testid="profile-gender-select">
            <option value="">Válassz...</option>
            <option value="ferfi">Férfi / Fiú</option>
            <option value="no">Nő / Lány</option>
          </select>
        </Field>
        <Field label="Születési dátum" missing={missing.includes("birth_date")} testId="profile-field-birth-date">
          <input type="date" value={form.birth_date} onChange={set("birth_date")} className={inputCls(missing.includes("birth_date"))} data-testid="profile-birth-date-input" />
        </Field>
        <Field label="E-mail-cím" missing={missing.includes("email")} testId="profile-field-email">
          <input type="email" value={form.email} onChange={set("email")} className={inputCls(missing.includes("email"))} data-testid="profile-email-input" />
        </Field>
        <Field label="Telefonszám" missing={missing.includes("phone")} testId="profile-field-phone">
          <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+36 30 123 4567" className={inputCls(missing.includes("phone"))} data-testid="profile-phone-input" />
        </Field>
        <Field label="Jelszó" testId="profile-field-password">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Új jelszó (hagyd üresen, ha nem változtatod)"
              className={`${loginInputClass} pr-12`}
              data-testid="profile-password-input"
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-600 hover:text-slate-600" data-testid="profile-password-toggle" aria-label="Jelszó megjelenítése">
              {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-600">A jelenlegi jelszavad biztonsági okból nem jelenik meg.</p>
        </Field>
        <button type="submit" className={saveButtonClass} disabled={saving} data-testid="profile-save-btn">{saving ? "Mentés..." : "Mentés"}</button>
      </form>

      <hr className="border-slate-200" />

      <section className="rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100 space-y-5" data-testid="linked-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-slate-900">Hozzárendelt profilok</h2>
          <button type="button" onClick={() => setChildOpen(true)} className={primaryButtonClass} data-testid="add-child-btn">
            <Plus size={16} /> Gyerekfiók hozzáadása
          </button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input value={identifier} onChange={(e) => setIdentifier(e.target.value.toUpperCase())} placeholder="Azonosító kód" className={`${loginInputClass} font-mono tracking-wider`} data-testid="link-identifier-input" />
          <button type="button" onClick={link} disabled={linking} className="h-12 shrink-0 rounded-xl border-2 border-sky-600 px-5 text-sm font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-60" data-testid="link-submit-btn">
            {linking ? "Küldés..." : "Hozzárendelés"}
          </button>
        </div>
        <p className="text-xs text-slate-700">A hozzárendelés adminisztrátori jóváhagyás után válik aktívvá.</p>

        {pending.length > 0 && (
          <ul className="space-y-2" data-testid="pending-requests">
            {pending.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-yellow-400 bg-yellow-50 px-4 py-2.5 text-sm" data-testid={`pending-request-${r.id}`}>
                <span className="flex items-center gap-2 text-slate-700"><Clock size={16} className="text-yellow-600" />
                  <span><strong>{r.type === "child" ? r.child_name : r.target_name}</strong> – {r.type === "child" ? "Gyerekfiók kérelem" : `Hozzárendelés (${r.target_identifier})`}</span>
                </span>
                <span className="shrink-0 rounded-full bg-yellow-200 px-2.5 py-0.5 text-xs font-semibold text-yellow-900">Jóváhagyásra vár</span>
              </li>
            ))}
          </ul>
        )}

        <div data-testid="linked-list">
          {linked.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-600" data-testid="linked-empty">Még nincs hozzárendelt fiók.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {linked.map((acc) => (
                <li key={acc.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`linked-row-${acc.id}`}>
                  <span className="flex-1 font-semibold text-slate-800">{acc.name} <span className="font-mono text-xs font-medium text-slate-700">({acc.identifier || "—"})</span></span>
                  <span className="text-slate-700">{acc.birth_date || "—"}</span>
                  <button type="button" onClick={() => unlink(acc)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100" data-testid={`unlink-btn-${acc.id}`}>
                    <Trash2 size={14} /> Törlés
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {childOpen && <ChildRequestModal onClose={() => setChildOpen(false)} onDone={() => { setChildOpen(false); load(); }} />}
    </div>
  );
}

function ChildRequestModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", birth_date: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.birth_date) { toast.error("Név és születési dátum megadása kötelező"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ugyfelkapu/child-requests`, form, { params: { token: token() } });
      toast.success(res.data.message);
      onDone();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="child-request-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl bg-white p-6 sm:p-8 shadow-2xl space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Gyerekfiók hozzáadása</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-600" data-testid="child-modal-close"><X size={22} /></button>
        </div>
        <p className="text-sm text-slate-700">A kérelem admin jóváhagyása után a fiók létrejön és automatikusan hozzád lesz rendelve.</p>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Név *</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Gyerek neve" className={loginInputClass} data-testid="child-name-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Születési dátum *</label>
          <input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className={loginInputClass} data-testid="child-birth-date-input" />
        </div>
        <button type="submit" className={`${primaryButtonClass} w-full h-12`} disabled={busy} data-testid="child-submit-btn">{busy ? "Küldés..." : "Kérelem beküldése"}</button>
      </form>
    </div>
  );
}

export default ProfilTab;
